import CustomAutoComplete, { CustomAutoCompleteProps } from '../../UI/CustomAutoComplete';
import { WorkingUnit } from '../../../types/WorkingUnit.interface';
import { useMemo } from 'react';
import { compareWorkingUnits } from '../../../utils';
import { AutocompleteValue } from '@mui/material/useAutocomplete/useAutocomplete';

export type WorkingUnitAutocompleteProps<Multiple extends boolean | undefined = true> = {
    workingUnitOptions: WorkingUnit[];
    selectedWorkingUnits: AutocompleteValue<WorkingUnit, Multiple, false, false>;
    onChange: (value: AutocompleteValue<WorkingUnit, Multiple, false, false>) => void;
    label?: string;
    isMultiSelect?: Multiple;
} & Omit<CustomAutoCompleteProps<WorkingUnit, Multiple>, 'options' | 'label' | 'value' | 'onChange' | 'labelKeys'>;

function WorkingUnitAutocomplete<Multiple extends boolean | undefined = true>({
    workingUnitOptions,
    selectedWorkingUnits,
    onChange,
    label,
    isMultiSelect,
    ...props
}: WorkingUnitAutocompleteProps<Multiple>) {
    const handleChange = (workingUnits: AutocompleteValue<WorkingUnit, Multiple, false, false>) => {
        if (isMultiSelect && Array.isArray(workingUnits)) {
            const allWorkingUnitsObject = workingUnits.find((workingUnit) => workingUnit.id === -1);
            if (allWorkingUnitsObject && workingUnits.length > 1) {
                if (workingUnits.indexOf(allWorkingUnitsObject) === workingUnits.length - 1) {
                    onChange([allWorkingUnitsObject] as AutocompleteValue<WorkingUnit, Multiple, false, false>);
                } else {
                    onChange(
                        workingUnits.filter(
                            (workingUnit) => workingUnit.id !== allWorkingUnitsObject.id
                        ) as AutocompleteValue<WorkingUnit, Multiple, false, false>
                    );
                }
                return;
            }
        }

        onChange(workingUnits);
    };

    const sortedOptions = useMemo(() => {
        return [...workingUnitOptions].sort(compareWorkingUnits);
    }, [workingUnitOptions]);

    return (
        <CustomAutoComplete<WorkingUnit, Multiple>
            {...props}
            isMultiSelect={isMultiSelect}
            options={sortedOptions}
            labelKeys={['worktype.worktype', 'channel.name', 'task.name']}
            value={selectedWorkingUnits}
            label={label || 'Working units'}
            onChange={handleChange}
            isOptionEqualToValue={(option: WorkingUnit, value: WorkingUnit) => option.id === value.id}
        />
    );
}

export default WorkingUnitAutocomplete;
